import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import { FormControlLabel, Typography } from '@material-ui/core'
import { DateTimePicker } from '@material-ui/pickers'
import moment, { Moment } from 'moment'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'

import { Dialog, snackbar } from 'components'
import { useApiCall, useCheckFormErrors } from 'hooks'

import { Event, UpdateEventPayload, UpdateEventResponse } from '../../types'
import { updateEvent } from '../../api'
import { CustomSwitch } from '../CustomSwitch'
import { useStyles } from './styles'

const rules = {
  name: [
    {
      validate: (name: string) => name.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  start: [
    {
      validate: (start?: Moment) => !!start,
      message: 'Seleccionar una fecha de incio',
    },
  ],
  end: [
    {
      validate: (start?: Moment) => !!start,
      message: 'Seleccionar una fecha de fin',
    },
  ],
}

interface UpdateEventDialogProps {
  handleClose: () => void
  event: Event
  onDone: (event: Event) => void
}

const UpdateEventDialog: React.FC<UpdateEventDialogProps> = ({ handleClose, onDone, event }) => {
  const classes = useStyles()

  const [name, setName] = useState(event.name)
  const [active, setActive] = useState(event.active)
  const [start, setStart] = useState<Moment>(moment(event.start))
  const [end, setEnd] = useState<Moment>(moment(event.end))

  const [updateEventApi, isLoading] = useApiCall<UpdateEventPayload, UpdateEventResponse>(
    updateEvent,
  )

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    setName(e.target.value)
  }

  const changeStartDate = (date: MaterialUiPickersDate) => {
    setStart(date as Moment)
  }

  const changeEndDate = (date: MaterialUiPickersDate) => {
    setEnd(date as Moment)
  }

  const toggleActive = () => {
    setActive(!active)
  }

  const fields = {
    name,
    start,
    end,
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(fields, rules, [
    'name',
    'start',
    'end',
  ])
  const isSubmitDisabled = hasErrors || isAnyFieldEmpty || isLoading

  const onUpdateEvent = async () => {
    try {
      const { event: updatedEvent } = await updateEventApi({
        id: event.id,
        name,
        start,
        end,
        active,
      })
      onDone(updatedEvent)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo editar el sorteo. Intente de nuevo.')
    }
  }

  return (
    <Dialog
      title="Editar sorteo"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Editar"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onUpdateEvent}
      className={classes.modal}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            label="Nombre"
            value={name}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            error={errors.name.hasError}
            helperText={errors.name.message}
          />
        </Grid>
        <Grid item xs={12}>
          <DateTimePicker
            label="Desde"
            value={start ?? null}
            onChange={changeStartDate}
            ampm={false}
            TextFieldComponent={props => <TextField {...props} />}
            format="DD/MM/YYYY HH:mm"
            clearable
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={12}>
          <DateTimePicker
            label="Hasta"
            value={end ?? null}
            onChange={changeEndDate}
            ampm={false}
            TextFieldComponent={props => <TextField {...props} />}
            format="DD/MM/YYYY HH:mm"
            clearable
            fullWidth
            required
          />
        </Grid>
        <Grid container item xs={12} direction="column">
          <FormControlLabel
            control={<CustomSwitch checked={active} onChange={toggleActive} required />}
            label="Activo"
          />
          <Typography variant="caption">*Solo puede haber un evento activado</Typography>
        </Grid>
      </Grid>
    </Dialog>
  )
}

export { UpdateEventDialog }
